import { connectStreamSource, StreamActions } from '@hotwired/turbo'

/**
 * Custom Turbo Stream actions for the site-wide notifications stream.
 *
 * The server broadcasts these over the notifications Mercure topic (<APP_URL>/notifications, see
 * src/Core/Manager/NotificationManager.php); the EventSource opened at the end of
 * init() receives them and Turbo invokes the matching `StreamActions[...]` handler
 * below. `this` is the <turbo-stream> element, so payload is read with
 * `this.getAttribute(...)`.
 *
 * This replaces the old EventSource listener (assets/js/listeners/notification.js)
 * and its JSON parsing — the transport is now pure Turbo + Mercure.
 *
 * @param {App} app
 */
export default function init({ app }) {
    // A new-version prompt dismisses the blocking maintenance modal, so both
    // actions share this reference.
    let maintenanceModal = null

    /**
     * action="maintenance" status="start|stop" — open/close a blocking modal so
     * the user can't keep editing while the site is down.
     */
    StreamActions.maintenance = function () {
        /** @type {ModalManager} */
        const modalManager = app.get('modalManager')

        if (this.getAttribute('status') === 'start') {
            maintenanceModal = modalManager.createModal({
                title: 'Maintenance',
                html: 'The site is currently under maintenance. Please do not refresh the page to avoid any data loss.',
                allowOutsideClick: false,
                showCloseButton: false,
                showCancelButton: false,
                showConfirmButton: false,
            })
        } else {
            maintenanceModal?.close()
        }
    }

    /**
     * action="new-version" version="1.2.3" — prompt the user to reload onto the
     * new build, respecting in-progress work (Refresh / Later).
     */
    StreamActions['new-version'] = async function () {
        maintenanceModal?.close()

        /** @type {ModalManager} */
        const modalManager = app.get('modalManager')
        const result = await modalManager.createPrompt({
            title: 'New version available',
            html: `The new version (<em>${this.getAttribute('version')}</em>) of the site is available. Please refresh the page to update.`,
            confirmButtonText: 'Refresh',
            cancelButtonText: 'Later',
        })

        if (result === true) {
            window.location.reload()
        }
    }

    // One EventSource for the tab's whole life, opened at boot rather than a <turbo-mercure-stream-source
    // data-turbo-permanent> in <body>: Turbo Drive moves permanent elements into the new <body> on every
    // visit, which disconnects + reconnects the element (dropping anything published in between). Not bound
    // to the DOM, this one survives visits, and the browser's own reconnects resend Last-Event-ID.
    connectStreamSource(new EventSource(app.get('topics').notifications))
}
